import { BillingPaymentScheduleType, BillingStatus } from "../../types/billings";
import {
  ICollectionPoint,
  ICollectionPointApi,
  ICollectionPointBilling,
  ICollectionPointBillingApi,
  ICollectionPointPut,
  PutCollectionPointPaymentArgs,
  PutCollectionPointRefundArgs,
} from "../../types/collectionPoint";
import { toDateString } from "../../utils/date";
import {
  adaptBillingPaymentScheduleForFrontend,
  adaptBillingPaymentsForFrontend,
  adaptBillingRefundsForFrontend,
} from "./billingsAdapt";

export const adaptCollectionPointBillingForFrontend = (
  apiCollectionPointBilling: ICollectionPointBillingApi | null
): ICollectionPointBilling | null => {
  if (!apiCollectionPointBilling) {
    return null;
  }

  return {
    amount: apiCollectionPointBilling.amount ? parseFloat(apiCollectionPointBilling.amount) : null,
    netAmount: apiCollectionPointBilling.net_amount ? parseFloat(apiCollectionPointBilling.net_amount) : null,
    isClosed: apiCollectionPointBilling.is_closed,
  };
};

export const adaptCollectionPointDetailResponseForFrontend = (
  apiCollectionPoint: ICollectionPointApi
): ICollectionPoint => {
  let billingStatus: ICollectionPoint["billingStatus"] = null;

  if (apiCollectionPoint.payments === null) {
    billingStatus = null;
  } else if (apiCollectionPoint.billing?.is_closed) {
    billingStatus = BillingStatus.Closed;
  } else if (apiCollectionPoint.payments.length === 0) {
    billingStatus = BillingStatus.New;
  } else {
    billingStatus = BillingStatus.Active;
  }

  return {
    locationId: apiCollectionPoint.locationId,
    administrationId: apiCollectionPoint.administrationId,
    name: apiCollectionPoint.name,
    address: apiCollectionPoint.address,
    householdMembersCount: apiCollectionPoint.householdMembersCount,
    degreeOfSeparation: apiCollectionPoint.degreeOfSeparation,
    wastePerHouseholdMember: apiCollectionPoint.wastePerHouseholdMember,
    containers: apiCollectionPoint.containers,
    isPO: apiCollectionPoint.isPO,
    invitationToken: apiCollectionPoint.invitationToken,
    invitationStatus: apiCollectionPoint.invitationStatus,
    isPaid: apiCollectionPoint.isPaid,
    year: apiCollectionPoint.year,
    disableTime: apiCollectionPoint.disableTime,
    enableTime: apiCollectionPoint.enableTime,
    billing: adaptCollectionPointBillingForFrontend(apiCollectionPoint.billing),
    billingStatus,
    paymentSchedule: adaptBillingPaymentScheduleForFrontend(
      apiCollectionPoint.year,
      apiCollectionPoint.paymentSchedule
    ),
    payments: adaptBillingPaymentsForFrontend(apiCollectionPoint.payments),
    refunds: adaptBillingRefundsForFrontend(apiCollectionPoint.refunds),
  };
};

export const adaptCollectionPointForBackend = (collectionPoint: ICollectionPoint): ICollectionPointPut => {
  const putCollectionPoint: ICollectionPointPut = {
    year: collectionPoint.year,
  };

  if (collectionPoint?.paymentSchedule?.type === BillingPaymentScheduleType.Annual) {
    putCollectionPoint.payment_schedule = {
      annual_due_date: toDateString(collectionPoint.paymentSchedule.annualDueDate),
    };
  } else if (collectionPoint?.paymentSchedule?.type === BillingPaymentScheduleType.Quarterly) {
    putCollectionPoint.payment_schedule = {
      q1_due_date: toDateString(collectionPoint.paymentSchedule.q1DueDate),
      q2_due_date: toDateString(collectionPoint.paymentSchedule.q2DueDate),
      q3_due_date: toDateString(collectionPoint.paymentSchedule.q3DueDate),
      q4_due_date: toDateString(collectionPoint.paymentSchedule.q4DueDate),
    };
  }

  return putCollectionPoint;
};

export const adaptCollectionPointPaymentForBackend = (
  collectionPoint: ICollectionPoint,
  args: PutCollectionPointPaymentArgs
) => {
  const putCollectionPoint: ICollectionPointPut = {
    year: collectionPoint.year,
  };
  putCollectionPoint.payments = [
    {
      period: args.period,
      disable: !args.isPaid,
    },
  ];
  return putCollectionPoint;
};

export const adaptCollectionPointRefundForBackend = (
  collectionPoint: ICollectionPoint,
  args: PutCollectionPointRefundArgs
) => {
  const putCollectionPoint: ICollectionPointPut = {
    year: collectionPoint.year,
  };
  putCollectionPoint.refunds = [
    {
      id: args.id,
      disable: !args.isReturned,
    },
  ];
  return putCollectionPoint;
};
